import React, { useState, useEffect } from "react";
import { Button, Form } from "react-bootstrap";
import { useCart } from "../context/CartContext";
import { useUser } from "../context/UserContext";
import "../styles/Checkout.css";
import md5 from "md5";
import axios from "axios";
import { BASE_URL } from "../config/config";
import { toast } from "react-toastify";

const Checkout = () => {
  const { cartItems } = useCart();
  const { company } = useUser();
  const token = sessionStorage.getItem("token");
  const [loading, setLoading] = useState(false);

  const generateTransactionId = () => {
    return `LRN-${crypto.randomUUID().split("-")[0].toUpperCase()}`;
  };

  const [order_id] = useState(generateTransactionId());

  const [data, setData] = useState({
    fname: "",
    lname: "",
    mobile_no: "",
    email: "",
    address: "",
    city: "",
    country: "",
    pincode: "",
    state: "",
    upi_id: "",
    merchantno: "",
    secretKey: "",
    callback_url: "",
    currency: "",
    description: "",
    pay_by: "",
    payment_mode: "",
  });

  const [error, setError] = useState({});

  /* ================= TOTAL ================= */
  const totalAmount = cartItems.reduce(
    (sum, item) => sum + Number(item.price || item.coursePrice || 0),
    0
  );
  
  useEffect(() => {
    if (company) {
      setData((prev) => ({
        ...prev,
        order_id: order_id,
        merchantno: company.merchantno || "",
        secretKey: company.secretKey || "",
        callback_url: company.callback_url || "",
        currency: company.currency || "",
        description: company.description || "",
        pay_by: company.pay_by || "",
        payment_mode: company.payment_mode || "",
        amount: totalAmount || "",
      }));
    }
  }, [company]);

  /* ================= VALIDATION ================= */
  const validate = () => {
    let err = {};

    if (!data.fname) err.fname = "First name required";
    if (!data.lname) err.lname = "Last name required";
    if (!/^[0-9]{10}$/.test(data.mobile_no)) err.mobile_no = "Valid mobile number required";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(data.email))
      err.email = "Valid email required";
    if (!data.address) err.address = "Address required";
    if (!data.pincode) err.pincode = "Pincode required";
    if (!data.city) err.city = "City required";
    if (!data.state) err.state = "State required";
    if (!data.country) err.country = "Country required";
    if (!data.upi_id)
      err.upi_id = "UPI ID required";
    // if (Number(totalAmount) > Number(company?.pay_range)) {
    //   toast.error(
    //     `Per transaction limit exceeded`
    //   );
    //   setLoading(false);
    //   return;
    // }

    setError(err);
    return Object.keys(err).length === 0;
  };

  /* ================= PINCODE AUTO-FILL ================= */
  useEffect(() => {
    if (data.pincode.length === 6) {
      fetch(`https://api.postalpincode.in/pincode/${data.pincode}`)
        .then((res) => res.json())
        .then((res) => {
          if (res[0]?.Status === "Success") {
            const info = res[0].PostOffice[0];
            setData((prev) => ({
              ...prev,
              city: info.District,
              state: info.State,
              country: info.Country,
            }));
          }
        });
    }
  }, [data.pincode]);

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    try {
      setLoading(true);
      if (!validate()) {
        setLoading(false);
        return;
      }
      // ✅ Token check
      if (!token) {
        toast.error("Session expired. Please login again.");
        window.location.href = "/";
        return;
      }

      const return_url = `${window.location.origin}/confirm-order/${order_id}`;

      // ✅ Checksum
      const checksumString = `${data.merchantno}#@#${totalAmount}@#@${order_id}#@#${data.mobile_no}@#@${return_url}#@#${data.secretKey}`;
      const checksum = md5(checksumString);

      const orderPayload = {
        ...data,
        order_id,
        return_url,
        checksum,
      };

      // console.log("Payload 👉", orderPayload);

        const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      };

      /* ================= STEP 1: SAVE ORDER ================= */
      let saveOrderRes;

      try {
        saveOrderRes = await axios.post(
          `${BASE_URL}/placeOrder`,
          orderPayload,
          config
        );

        if (saveOrderRes.status !== 200) {
          throw new Error("Order save failed");
        }

        // console.log("Order saved ✅");
      } catch (err) {
        console.error("Order Save Error ❌", err);

        if (err.response?.status === 401) {
          sessionStorage.removeItem("token");
          toast.error("Session expired. Please login again.");
          window.location.href = "/";
          return;
        }

        toast.error(
          err.response?.data?.message || "Failed to save order"
        );
        return; // ❗ stop execution
      }

      /* ================= STEP 2: PAYMENT ================= */
      let paymentRes;

      try {
        paymentRes = await axios.post(
          company.payment_url,
          orderPayload
        );

        const result = paymentRes.data;

        console.log("Payment Response 👉", result);

        if (result?.status === "SUCCESS" && result?.payment_url) {
          window.location.href = result.payment_url;
          return;
        }

        // ❌ Payment failed
        toast.error(result?.FailCause || "Payment failed");
      } catch (err) {
        console.error("Payment Error ❌", err);

        toast.error(
          err.response?.data?.message ||
            "Payment service error. Try again."
        );
      }

    } catch (error) {
      console.error("Unexpected Error ❌", error);

      toast.error(
        error?.message || "Something went wrong ❌"
      );
    } finally {
      setLoading(false); // ✅ always stop loading
    }
  };

  /* ================= EMPTY CART ================= */
  if (cartItems.length === 0) {
    return (
      <div className="container mt-5 text-center">
        <h3>Your cart is empty 🛒</h3>
      </div>
    );
  }

  return (
    <div className="container py-5 mt-5">
      <h1 className="fw-bold display-5 text-center">Almost Yours!</h1>
      <p className="fs-5 text-center mb-4">
        You're just one step away from completing your order
      </p>

      <Form onSubmit={handleSubmit}>
        <div className="row g-4">
          {/* ================= BILLING ================= */}
          <div className="col-lg-7">
            <div className="p-4 rounded shadow bg-white">
              <h4 className="mb-4 fw-semibold border-bottom pb-2">
                Billing Information
              </h4>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    value={data.fname}
                    onChange={(e) =>
                      setData({ ...data, fname: e.target.value })
                    }
                  />
                  <small className="text-danger">{error.fname}</small>
                </div>

                <div className="col-md-6 mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    value={data.lname}
                    onChange={(e) =>
                      setData({ ...data, lname: e.target.value })
                    }
                  />
                  <small className="text-danger">{error.lname}</small>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    value={data.mobile_no}
                    onChange={(e) =>
                      setData({ ...data, mobile_no: e.target.value })
                    }
                  />
                  <small className="text-danger">{error.mobile_no}</small>
                </div>

                <div className="col-md-6 mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    value={data.email}
                    onChange={(e) =>
                      setData({ ...data, email: e.target.value })
                    }
                  />
                  <small className="text-danger">{error.email}</small>
                </div>
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Address</Form.Label>
                <Form.Control
                  value={data.address}
                  onChange={(e) =>
                    setData({ ...data, address: e.target.value })
                  }
                />
                <small className="text-danger">{error.address}</small>
              </Form.Group>

              <div className="row">
                <div className="col-md-4 mb-3">
                  <Form.Label>Pincode</Form.Label>
                  <Form.Control
                    value={data.pincode}
                    onChange={(e) =>
                      setData({ ...data, pincode: e.target.value })
                    }
                  />
                  <small className="text-danger">{error.pincode}</small>
                </div>

                <div className="col-md-4 mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    value={data.city}
                    onChange={(e) =>
                      setData({ ...data, city: e.target.value })
                    }
                  />
                  <small className="text-danger">{error.city}</small>
                </div>

                <div className="col-md-4 mb-3">
                  <Form.Label>State</Form.Label>
                  <Form.Control
                    value={data.state}
                    onChange={(e) =>
                      setData({ ...data, state: e.target.value })
                    }
                  />
                  <small className="text-danger">{error.state}</small>
                </div>
              </div>

              <Form.Group>
                <Form.Label>Country</Form.Label>
                <Form.Control
                  value={data.country}
                  onChange={(e) =>
                    setData({ ...data, country: e.target.value })
                  }
                />
                <small className="text-danger">{error.country}</small>
              </Form.Group>
            </div>
          </div>

          {/* ================= ORDER SUMMARY ================= */}
          <div className="col-lg-5">
            <div className="p-4 rounded shadow bg-white position-sticky top-5">
              <h4 className="fw-semibold border-bottom pb-2 mb-3">
                🛒 Order Summary
              </h4>

              {cartItems.map((item) => (
                <div
                  key={item.product_id}
                  className="order-item d-flex justify-content-between align-items-start"
                >
                  <span className="order-title">
                    {item.product_name || item.courseName}
                  </span>
                  <span className="order-price">
                    ₹ {item.price || item.coursePrice}
                  </span>
                </div>
              ))}

              <hr />

              <div className="d-flex justify-content-between fw-bold fs-5 mb-3">
                <span>Total</span>
                <span className="text-success">₹ {totalAmount}</span>
              </div>

              <h5 className="fw-semibold mt-4 mb-3">Payment Method</h5>

              <div className="payment-options">

                {/* UPI */}
                <label className={`payment-card active`}>
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <Form.Check
                        type="radio"
                        checked
                        readOnly
                        label={<span className="fw-semibold">UPI</span>}
                      />
                      <small className="text-muted">Pay using any UPI app</small>
                    </div>
                    <span className="badge bg-success">Recommended</span>
                  </div>

                  <Form.Control
                    className="mt-3"
                    placeholder="example@upi"
                    value={data.upi_id}
                    onChange={(e) =>
                      setData({ ...data, upi_id: e.target.value })
                    }
                  />
                  <small className="text-danger">{error.upi_id}</small>
                </label>

                {/* Net Banking */}
                {/* <label className="payment-card disabled">
                  <div className="d-flex align-items-center justify-content-between">
                    <Form.Check
                      type="radio"
                      disabled
                      label={<span className="fw-semibold">Net Banking</span>}
                    />
                    <span className="badge bg-secondary">Coming Soon</span>
                  </div>
                  <small className="text-muted">All major banks supported</small>
                </label> */}

                {/* Card */}
                {/* <label className="payment-card disabled">
                  <div className="d-flex align-items-center justify-content-between">
                    <Form.Check
                      type="radio"
                      disabled
                      label={<span className="fw-semibold">Credit / Debit Card</span>}
                    />
                    <span className="badge bg-secondary">Coming Soon</span>
                  </div>
                  <small className="text-muted">Visa, Mastercard, RuPay</small>
                </label> */}

              </div>

              <Button
                type="submit"
                className="w-100 btn-lg mt-4 btn-place-order"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    />
                    Processing...
                  </>
                ) : (
                  "Place Order"
                )}
              </Button>
            </div>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default Checkout;